import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { AppDispatch, RootState } from 'store/store';
import { notification } from 'antd';
import { formatFullName, hasPermission, transformNullToEmptyString } from 'utils/utils';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { DRAWER_WIDTH } from 'constants/ui';
import { Role } from 'types/roleTypes';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import RoleForm from './tabs/RoleForm';
import UserForm from './tabs/UserForm';
import { ScopeResource } from 'types/scopeResourcesTypes';
import ScopeForm from './tabs/ScopeForm';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { ROLE_CREATED_ID, ROLE_EDITED_ID } from 'constants/userActivities';
import { fetchTokenIdentity } from 'store/slices/authSlice';
import { User } from 'types/userTypes';
import { useTranslation } from 'react-i18next';

interface RolesDrawerProps {
    open: boolean;
    selectedRole: Role | null;
    onClose: () => void;
    setRoles: (roles: Role[]) => void;
}

const RolesDrawer: React.FC<RolesDrawerProps> = ({ open, selectedRole, onClose, setRoles }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const token = useSelector((state: RootState) => state.auth.auth.data[0].token);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const hasRoleWrite = hasPermission(tokenScopeList, orgId, 'o', 'role:w');

    const [activeKey, setActiveKey] = useState<string>('role');
    const [scopeResources, setScopeResources] = useState<ScopeResource[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [usersInRole, setUsersInRole] = useState<User[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [usersLoading, setUsersLoading] = useState<boolean>(false);

    const [form] = OpForm.useForm();

    const { t } = useTranslation();
    useEffect(() => {
        if (open) {
            setActiveKey('role');
        }
    }, [open]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setUsersLoading(true);
            try {
                const usersRequests: [Promise<any>, Promise<any> | null] = [
                    getRequest(`/orgs/${orgId}/users`),
                    selectedRole ? getRequest(`/orgs/${orgId}/roles/${selectedRole.id}/users`) : null,
                ];

                const [usersRes, usersInRoleRes] = await Promise.all(usersRequests);
                setUsers(usersRes.data.filter((user: User) => user.status === 1));
                setUsersInRole(usersInRoleRes ? usersInRoleRes.data.filter((user: User) => user.status === 1) : []);
            } catch (error) {
                console.log("Failed to fetch users data.");
            } finally {
                setUsersLoading(false);
            }

            try {
                const scopeResourcesRes = await getRequest(`/orgs/${orgId}/scopeResources`);
                setScopeResources(scopeResourcesRes.data);
            } catch (error) {
                console.log("Failed to fetch scope resources.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId, selectedRole]);

    const initialValues = {
        ...(selectedRole ? {
            name: selectedRole?.name,
            description: selectedRole?.description
        } : {}),
        ...scopeResources.reduce<Record<string, boolean>>((acc, accessControl) => {
            accessControl.scopes.forEach(scope => {
                acc[`read-${scope.id}`] = !!(selectedRole?.scopes.includes(scope.id) && scope.permission === 'READ');
                acc[`write-${scope.id}`] = !!(selectedRole?.scopes.includes(scope.id) && scope.permission === 'WRITE');
            });
            return acc;
        }, {})
    };

    const tabItems = [
        {
            key: 'role',
            label: t('role.tabs.role'),
            children: <RoleForm />,
        },
        ...(selectedRole ? [{
            key: 'user',
            label: t('role.tabs.user'),
            // Remount UserForm once usersInRole is finalized so its internal
            // targetKeys state initializes with the correct value in a single
            // commit. Without this, the transfer first renders with empty
            // targetKeys and then visibly animates rows from the available column
            // into the in-role column.
            children: (
                <UserForm
                    key={`${selectedRole.id}:${usersInRole.map((user) => user.id).join(',')}`}
                    users={users}
                    usersInRole={usersInRole}
                    loading={usersLoading}
                />
            ),
        }] : []),
        ...(selectedRole ? [{
            key: 'scope',
            label: t('role.tabs.scope'),
            children: <ScopeForm form={form} selectedRole={selectedRole} scopeResources={scopeResources} />,
        }] : [])
    ];

    const handleSubmit = useCallback(async ({ values, touchedValues, initialValues }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        let scopeToRemove: number[] = [];
        try {
            const transformedTouchedValues = transformNullToEmptyString(touchedValues);
            if (transformedTouchedValues && Object.keys(transformedTouchedValues).length > 0) {
                if (selectedRole) {
                    // Role
                    if (transformedTouchedValues.name != null || transformedTouchedValues.description != null) {
                        await patchRequest(`/orgs/${orgId}/roles/${selectedRole.id}`, {
                            name: transformedTouchedValues.name,
                            description: transformedTouchedValues.description
                        });
                    }

                    // Users
                    if (transformedTouchedValues.users != null) {
                        const usersToAdd: number[] = transformedTouchedValues.users
                            .filter((key: number) => !usersInRole.some(user => user.id === key));

                        const usersToRemove: number[] = usersInRole
                            .filter(user => !transformedTouchedValues.users.includes(user.id))
                            .map(user => user.id);

                        // Super Admin role validation
                        if (selectedRole.admin && usersToRemove.length > 0) {
                            const remainingSuperAdmins = usersInRole.length - usersToRemove.length + usersToAdd.length;
                            if (remainingSuperAdmins < 1) {
                                notification.error({
                                    message: t('notification.error'),
                                    description: t('role.notification.deleteErrorAtLeastOneUser', { roleName: selectedRole.name }),
                                    placement: 'bottomRight',
                                });
                                return;
                            }
                        }

                        // Check if removing users are having only this role
                        const usersWithoutOtherRoles = usersToRemove.filter(userId => {
                            const user = usersInRole.find(user => Number(user.id) === userId);
                            return user !== undefined && user.roleCount === 1;
                        });


                        if (usersWithoutOtherRoles.length > 0) {
                            const userNames = usersWithoutOtherRoles.map(userId => {
                                const user = usersInRole.find(user => Number(user.id) === userId);
                                return user ? formatFullName(user.identity.firstName, user.identity.middleName, user.identity.lastName) : 'Unknown';
                            }).join(', ');

                            notification.error({
                                message: t('notification.error'),
                                description: t('role.notification.deleteErrorOnlyRole', { userNames: userNames }),
                                placement: 'bottomRight',
                            });
                            return;
                        }

                        const payload = {
                            ...(usersToAdd.length > 0 && { add: usersToAdd }),
                            ...(usersToRemove.length > 0 && { remove: usersToRemove }),
                        };

                        if (Object.keys(payload).length) {
                            await patchRequest(`/orgs/${orgId}/roles/${selectedRole.id}/userIds`, payload);
                        }
                    }

                    // Scopes
                    if (Object.keys(transformedTouchedValues).some((key) => key.includes('-'))) {
                        if (selectedRole.admin) {
                            notification.error({
                                message: t('notification.error'),
                                description: t('role.notification.deleteErrorCanNotModify', { roleName: selectedRole.name }),
                                placement: 'bottomRight',
                            });
                            return;
                        }

                        const scopesToAdd: number[] = [];

                        Object.entries(values).forEach(([key, value]) => {
                            if (key.includes('-')) {
                                const [type, id] = key.split('-');
                                if (type === 'read' || type === 'write') {
                                    const isChecked = Boolean(value);
                                    const scopeId = parseInt(id, 10);
                                    const currentState = initialValues![key];
                                    if (isChecked && !currentState) {
                                        scopesToAdd.push(scopeId);
                                    } else if (!isChecked && currentState) {
                                        scopeToRemove.push(scopeId);
                                    }
                                }
                            }
                        });
                        await patchRequest(`/orgs/${orgId}/roles/${selectedRole.id}/scopeIds`, {
                            add: scopesToAdd,
                            remove: scopeToRemove
                        });
                    }
                    await dispatch(fetchTokenIdentity({ token }));

                    const getUserActivityWriteScopeId = (scopeResources: ScopeResource[]): number | null => {
                        for (const resource of scopeResources) {
                            for (const scope of resource.scopes) {
                                if (scope.name === 'Activity Log' && scope.permission === 'WRITE') {
                                    return scope.id;
                                }
                            }
                        }
                        return null;
                    };

                    const userActivityWriteScopeId = getUserActivityWriteScopeId(scopeResources);
                    const isRemovingActivityWrite = userActivityWriteScopeId !== null && scopeToRemove.includes(userActivityWriteScopeId);

                    if (!isRemovingActivityWrite) {
                        await postRequest(`/orgs/${orgId}/userActivity`, {
                            userId: globalUserId,
                            activityId: ROLE_EDITED_ID,
                            details: values.name
                        });
                    }

                    notification.success({
                        message: t('notification.success'),
                        description: t('role.notification.updateSuccess'),
                        placement: 'bottomRight',
                    });
                } else {
                    await postRequest(`/orgs/${orgId}/roles`, {
                        name: transformedTouchedValues.name,
                        description: transformedTouchedValues.description
                    });
                    await postRequest(`/orgs/${orgId}/userActivity`, {
                        userId: globalUserId,
                        activityId: ROLE_CREATED_ID,
                        details: values.name
                    });
                    notification.success({
                        message: t('notification.success'),
                        description: t('role.notification.createSuccess'),
                        placement: 'bottomRight',
                    });
                }

                const updatedRoles = await getRequest(`/orgs/${orgId}/roles`);
                const filteredUpdatedRoles = updatedRoles.data.filter((role: Role) => role.status === 1);
                setRoles(filteredUpdatedRoles);

            }
            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('role.notification.saveError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [dispatch, selectedRole, t, orgId, globalUserId, form, onClose, setRoles, token, usersInRole, scopeResources]);

    return (
        <OpFormDrawer
            form={form}
            title={selectedRole ? selectedRole.name : t('role.addTitle')}
            width={DRAWER_WIDTH}
            open={open}
            onClose={onClose}
            isFormLoading={loading || isSubmitting}
            isFormReadOnly={!hasRoleWrite}
            formComponent={
                <OpForm
                    form={form}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    hasError={false}
                    defaultButtons={false}
                    isReadOnly={!hasRoleWrite}
                >
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                </OpForm>
            }
        />
    );
}

export default RolesDrawer;
